(() => {
  const SELECTORS = {
    section: '.shop-the-parts--build-showcase',
    filter: '[data-build-parts-filter]',
    button: '[data-build-parts-filter-value]',
    result: '[data-build-parts-filter-result]',
    item: '[data-build-part-subtype]',
    card: '.build-part-card',
    grid: '.build-parts-static-grid',
    typeSection: '[data-build-parts-type-section]',
    progress: '[data-build-parts-progress]',
    progressCurrent: '[data-build-parts-progress-current]',
    progressTotal: '[data-build-parts-progress-total]',
    progressBar: '[data-build-parts-progress-bar]',
    sentinel: '[data-build-parts-load-sentinel]'
  };

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const matchingItems = (section) => {
    const value = section.dataset.activeBuildPartFilter || 'all';
    return Array.from(section.querySelectorAll(SELECTORS.item)).filter((item) => (
      value === 'all' || item.dataset.buildPartSubtype === value
    ));
  };

  const updateStickyProgress = (section) => {
    const progress = section.querySelector(SELECTORS.progress);
    if (!progress) return;

    const isThemePreview = Boolean(
      window.Shopify?.designMode || window.Shopify?.theme?.role === 'unpublished'
    );
    progress.style.bottom = isThemePreview
      ? 'max(76px, calc(env(safe-area-inset-bottom) + 76px))'
      : '';

    if (progress.hidden) {
      progress.classList.remove('is-visible');
      return;
    }

    const rect = section.getBoundingClientRect();
    const inSection = rect.top < window.innerHeight * 0.72 && rect.bottom > window.innerHeight * 0.28;
    progress.classList.toggle('is-visible', inSection);
  };

  const updateProgress = (section) => {
    const progress = section.querySelector(SELECTORS.progress);
    if (!progress) return;

    const items = matchingItems(section);
    const displayed = items.filter((item) => (
      item.querySelector(SELECTORS.card)?.classList.contains('is-revealed')
    )).length;
    const total = items.length;
    const hasScrollableResults = total > 10;

    progress.hidden = !hasScrollableResults;
    progress.querySelector(SELECTORS.progressCurrent).textContent = String(displayed);
    progress.querySelector(SELECTORS.progressTotal).textContent = String(total);
    progress.querySelector(SELECTORS.progressBar).style.width = `${total ? (displayed / total) * 100 : 100}%`;

    updateStickyProgress(section);
  };

  const buildTypeSections = (section) => {
    if (!section.classList.contains('shop-the-parts--group-by-type')) return;

    const grid = section.querySelector(SELECTORS.grid);
    if (!grid || grid.dataset.buildPartsTypeSectionsReady === 'true') return;

    const items = Array.from(grid.children).filter((child) => child.matches(SELECTORS.item));
    if (!items.length) return;

    const desktopColumns = Math.max(
      1,
      Number.parseInt(getComputedStyle(section).getPropertyValue('--build-parts-static-desktop-columns'), 10) || 5
    );
    const groups = new Map();

    items.forEach((item) => {
      const key = item.dataset.buildPartSubtype || 'other-parts';
      const label = item.dataset.buildPartTypeLabel || 'Other parts';

      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key).items.push(item);
    });

    groups.forEach((group, key) => {
      const typeSection = document.createElement('section');
      const heading = document.createElement('h3');
      const typeGrid = document.createElement('div');

      typeSection.className = 'build-parts-type-section';
      typeSection.dataset.buildPartsTypeSection = key;
      typeSection.style.setProperty('--build-parts-type-span-desktop', String(Math.min(Math.max(group.items.length, 2), desktopColumns)));
      typeSection.style.setProperty('--build-parts-type-span-tablet', String(Math.min(Math.max(group.items.length, 2), 3)));
      heading.className = 'build-parts-type-section__heading';
      heading.textContent = group.label;
      typeGrid.className = 'build-parts-type-section__grid';
      typeGrid.dataset.buildPartsTypeGrid = '';

      group.items.forEach((item) => typeGrid.appendChild(item));
      typeSection.append(heading, typeGrid);
      grid.appendChild(typeSection);
    });

    grid.dataset.buildPartsTypeSectionsReady = 'true';
  };

  const updateTypeSections = (section) => {
    section.querySelectorAll(SELECTORS.typeSection).forEach((typeSection) => {
      typeSection.hidden = !Array.from(typeSection.querySelectorAll(SELECTORS.item)).some((item) => !item.hidden);
    });
  };

  const revealRow = (section, cards) => {
    requestAnimationFrame(() => {
      cards.forEach((card) => card.classList.add('is-revealed'));
      updateProgress(section);
    });
  };

  const coordinateRowReveals = (section, reset = false) => {
    section.buildPartRowObserver?.disconnect();

    const positionedItems = Array.from(section.querySelectorAll(`${SELECTORS.item}:not([hidden])`))
      .filter((item) => item.getClientRects().length)
      .map((item) => {
        const rect = item.getBoundingClientRect();
        return {
          item,
          card: item.querySelector(SELECTORS.card),
          top: rect.top,
          left: rect.left
        };
      })
      .filter(({ card }) => card)
      .sort((a, b) => a.top - b.top || a.left - b.left);

    if (!positionedItems.length) return;

    const visualRows = [];
    const rowTolerance = 12;

    positionedItems.forEach((positionedItem) => {
      let row = visualRows.find((candidate) => Math.abs(candidate.top - positionedItem.top) <= rowTolerance);

      if (!row) {
        row = { top: positionedItem.top, items: [] };
        visualRows.push(row);
      }

      row.items.push(positionedItem);
    });

    const rows = visualRows.map((row) => row.items
      .sort((a, b) => a.left - b.left)
      .map(({ card }, index) => {
        if (reset) card.classList.remove('is-revealed');
        card.classList.add('dpp-reveal-ready');
        card.style.setProperty('--dpp-build-card-reveal-delay', `${index * 90}ms`);
        return card;
      }));

    const pendingRows = rows.filter((cards) => cards.some((card) => !card.classList.contains('is-revealed')));

    if (!pendingRows.length) return;

    if (!('IntersectionObserver' in window) || reducedMotion.matches) {
      pendingRows.forEach((cards) => revealRow(section, cards));
      return;
    }

    const cardsByTrigger = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const cards = cardsByTrigger.get(entry.target);
        if (cards) revealRow(section, cards);
        observer.unobserve(entry.target);
        cardsByTrigger.delete(entry.target);
      });
    }, {
      rootMargin: '0px 0px -4% 0px',
      threshold: 0.18
    });

    pendingRows.forEach((cards) => {
      const trigger = cards[0].closest(SELECTORS.item);
      if (!trigger) return;
      cardsByTrigger.set(trigger, cards);
      observer.observe(trigger);
    });

    section.buildPartRowObserver = observer;
  };

  const renderBatch = (section, reset = false) => {
    const items = matchingItems(section);
    const itemIndexes = new Map(items.map((item, index) => [item, index]));
    const batchSize = Number(section.dataset.buildPartsBatchSize) || 10;
    const previousShown = reset ? 0 : Number(section.dataset.buildPartsShown || 0);
    const shown = Math.min(items.length, reset ? batchSize : previousShown + batchSize);
    if (reset) {
      section.querySelectorAll(SELECTORS.card).forEach((card) => card.classList.remove('is-revealed'));
    }

    section.querySelectorAll(SELECTORS.item).forEach((item) => {
      const matchingIndex = itemIndexes.get(item);
      const visible = matchingIndex !== undefined && matchingIndex < shown;
      item.hidden = !visible;

    });

    updateTypeSections(section);

    section.dataset.buildPartsShown = String(shown);
    section.dataset.hasMoreBuildParts = String(shown < items.length);

    const result = section.querySelector(SELECTORS.result);
    if (result) result.textContent = `${items.length} ${items.length === 1 ? 'PART' : 'PARTS'}`;

    coordinateRowReveals(section, reset);
    updateProgress(section);
  };

  const applyFilter = (section, value) => {
    section.dataset.activeBuildPartFilter = value;
    renderBatch(section, true);
    rearmLoadSentinel(section);
  };

  const selectButton = (filter, activeButton) => {
    filter.querySelectorAll(SELECTORS.button).forEach((candidate) => {
      const isActive = candidate === activeButton;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-pressed', String(isActive));
    });
  };

  const selectFirstMobileSubtype = (section) => {
    if (!mobileQuery.matches) return;

    const filter = section.querySelector(SELECTORS.filter);
    if (!filter) return;

    const activeButton = filter.querySelector(`${SELECTORS.button}.is-active`);
    if (activeButton?.dataset.buildPartsFilterValue !== 'all') return;

    const firstSubtype = filter.querySelector(`${SELECTORS.button}:not([data-build-parts-filter-value="all"])`);
    if (!firstSubtype) return;

    selectButton(filter, firstSubtype);
    applyFilter(section, firstSubtype.dataset.buildPartsFilterValue);
  };

  const loadNextBatch = (section) => {
    if (section.dataset.hasMoreBuildParts !== 'true' || section.dataset.buildPartsLoading === 'true') return;

    section.dataset.buildPartsLoading = 'true';
    renderBatch(section);

    window.setTimeout(() => {
      section.dataset.buildPartsLoading = 'false';
      checkAutoLoad(section);
    }, 450);
  };

  const isSentinelNearViewport = (section) => {
    const sentinel = section.querySelector(SELECTORS.sentinel);
    if (!sentinel) return false;

    const rect = sentinel.getBoundingClientRect();
    return rect.top <= window.innerHeight + 240 && rect.bottom >= -40;
  };

  const checkAutoLoad = (section) => {
    if (isSentinelNearViewport(section)) loadNextBatch(section);
  };

  function rearmLoadSentinel(section) {
    const sentinel = section.querySelector(SELECTORS.sentinel);
    const observer = section.buildPartLoadObserver;
    if (!sentinel || !observer) return;

    observer.unobserve(sentinel);
    observer.observe(sentinel);
    requestAnimationFrame(() => checkAutoLoad(section));
  }

  const observeLoadSentinel = (section) => {
    const sentinel = section.querySelector(SELECTORS.sentinel);
    if (!sentinel || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNextBatch(section);
    }, { rootMargin: '0px 0px 240px 0px' });

    observer.observe(sentinel);
    section.buildPartLoadObserver = observer;
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(`${SELECTORS.section}:not([data-build-parts-filter-ready])`).forEach((section) => {
      const filter = section.querySelector(SELECTORS.filter);
      section.dataset.buildPartsFilterReady = 'true';
      section.dataset.activeBuildPartFilter = 'all';
      buildTypeSections(section);

      if (filter) {
        filter.addEventListener('click', (event) => {
          const button = event.target.closest(SELECTORS.button);
          if (!button) return;
          selectButton(filter, button);
          applyFilter(section, button.dataset.buildPartsFilterValue || 'all');
        });
      }

      if (mobileQuery.matches) selectFirstMobileSubtype(section);
      else renderBatch(section, true);

      observeLoadSentinel(section);
    });
  };

  let scrollFrame = 0;
  const updateOnScroll = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      document.querySelectorAll(SELECTORS.section).forEach((section) => {
        updateStickyProgress(section);
        checkAutoLoad(section);
      });
    });
  };

  let resizeTimer = 0;
  const updateOnResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll(SELECTORS.section).forEach((section) => {
        selectFirstMobileSubtype(section);
        coordinateRowReveals(section);
        updateStickyProgress(section);
      });
    }, 120);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('scroll', updateOnScroll, { passive: true });
  window.addEventListener('resize', updateOnResize);
})();
